<script setup lang="ts">
import { computed, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import http from '@/services/http';
import FilterEnumSelect from '@/components/FilterBuilder/FilterEnumSelect.vue';
import { ID_LOOKUP_OPTIONS, type LookupFilterOption } from '@/constants/lookupFilterOptions';

/**
 * A single-value picker for a lookup id, such as a list's status.
 *
 * The filter builder's lookup control is an "is one of" multi-select. A bulk edit sets exactly one
 * value, so this is a plain select. The options come from the bundled `ID_LOOKUP_OPTIONS` when the
 * slug is there. Otherwise they come from `GET /api/{slug}`, as the capabilities contract says.
 */
const props = defineProps<{
  slug: string;
  testIdPrefix?: string;
}>();

/** The chosen id, as a string, the way the select hands it over. */
const modelValue = defineModel<string | null>({ default: null });

const emit = defineEmits<{
  /** The chosen option's name, so the cached rows can show it before the search catches up. */
  (e: 'label', label: string | null): void;
}>();

const bundledOptions = computed(() => ID_LOOKUP_OPTIONS[props.slug] ?? null);

const optionsQuery = useQuery({
  queryKey: computed(() => ['bulk-lookup-options', props.slug] as const),
  queryFn: async (): Promise<LookupFilterOption[]> => {
    const response = await http.get(`/api/${props.slug}`);
    const body = response.data as { data?: unknown } | unknown[];
    const rows = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
    return (rows as Record<string, unknown>[])
      .filter((row) => Number.isInteger(Number(row.id)))
      .map((row) => ({
        id: Number(row.id),
        display_string: String(row.display_string ?? row.name ?? `#${row.id}`),
      }));
  },
  enabled: computed(() => bundledOptions.value === null),
  staleTime: 5 * 60 * 1000,
  retry: 0,
});

const options = computed<readonly LookupFilterOption[]>(
  () => bundledOptions.value ?? optionsQuery.data.value ?? [],
);

watch(
  [modelValue, options],
  ([value]) => {
    const chosen = options.value.find((option) => String(option.id) === value);
    emit('label', chosen?.display_string ?? null);
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <FilterEnumSelect
      v-model="modelValue"
      :options="[...options]"
      :get-value="(option: LookupFilterOption) => String(option.id)"
      :get-label="(option: LookupFilterOption) => option.display_string"
      :placeholder="optionsQuery.isLoading.value && bundledOptions === null ? 'Loading…' : 'Choose…'"
      :test-id-prefix="testIdPrefix ?? `bulk-lookup-${slug}`"
    />
    <p v-if="optionsQuery.isError.value" class="text-destructive mt-1 text-xs">
      Couldn't load the choices. Close the dialog and try again.
    </p>
  </div>
</template>
